import { accountsSelectors } from 'app-components/flux/accounts';
import { getBalancesByAccountIds } from 'app-components/flux/calculated-balances/balanceSelectors';
import { getGroupForAccount, type ReportAccountGroup } from 'app-components/flux/netWorth/netWorthSelectors';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';
import { toFiniteNumberOrNull } from '../utils';

const log = getLogger('llmNetWorthTools');

const DEFAULT_ACCOUNT_LIMIT = 8;
const MAX_ACCOUNT_LIMIT = 20;

const LIABILITY_GROUPS = new Set<ReportAccountGroup>([
  'Credit',
  'Home Loan',
  'Personal Loan',
  'Vehicle Loan',
  'Other Liabilities',
]);

export type NetWorthSummaryAccount = {
  name?: string | null;
  balance: number;
  group: ReportAccountGroup;
};

export type NetWorthSummarySide = 'asset' | 'liability';

export type NetWorthSummary = {
  netWorth: number;
  assets: number;
  liabilities: number;
  accountCount: number;
  groups: Array<{ name: ReportAccountGroup; total: number; side: NetWorthSummarySide }>;
  largestAccounts: Array<{
    name: string;
    group: ReportAccountGroup;
    balance: number;
    side: NetWorthSummarySide;
    shareOfSidePercent: number | null;
  }>;
};

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const shareOfSidePercent = (balance: number, sideTotal: number): number | null => {
  if (sideTotal === 0) return null;
  const share = (Math.abs(balance) / Math.abs(sideTotal)) * 100;
  if (!Number.isFinite(share)) return null;
  return Math.round(share * 10) / 10;
};

const sideForGroup = (group: ReportAccountGroup): NetWorthSummarySide =>
  LIABILITY_GROUPS.has(group) ? 'liability' : 'asset';

const accountLimit = (value: unknown): number => {
  const parsed = toFiniteNumberOrNull(value);
  if (parsed === null) return DEFAULT_ACCOUNT_LIMIT;
  return Math.min(MAX_ACCOUNT_LIMIT, Math.max(1, Math.trunc(parsed)));
};

/** Current net worth from account balances. Does not read generated reports. */
export const buildNetWorthSummary = (accounts: readonly NetWorthSummaryAccount[], limit: number): NetWorthSummary => {
  let assets = 0;
  let liabilities = 0;
  const groupTotals = new Map<ReportAccountGroup, number>();

  for (const account of accounts) {
    const balance = roundMoney(account.balance);
    if (sideForGroup(account.group) === 'liability') {
      liabilities += balance;
    } else {
      assets += balance;
    }
    groupTotals.set(account.group, roundMoney((groupTotals.get(account.group) ?? 0) + balance));
  }

  assets = roundMoney(assets);
  liabilities = roundMoney(liabilities);

  const groups = [...groupTotals.entries()]
    .filter(([, total]) => total !== 0)
    .map(([name, total]) => ({ name, total, side: sideForGroup(name) }))
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

  const largestAccounts = [...accounts]
    .filter((account) => account.balance !== 0 && account.name)
    .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
    .slice(0, limit)
    .map((account) => {
      const side = sideForGroup(account.group);
      const sideTotal = side === 'liability' ? liabilities : assets;
      return {
        name: account.name ?? '',
        group: account.group,
        balance: roundMoney(account.balance),
        side,
        shareOfSidePercent: shareOfSidePercent(account.balance, sideTotal),
      };
    });

  return {
    netWorth: roundMoney(assets + liabilities),
    assets,
    liabilities,
    accountCount: accounts.length,
    groups,
    largestAccounts,
  };
};

export const netWorthTools = {
  get_net_worth: {
    definition: {
      name: 'get_net_worth',
      description:
        'Current net worth from account balances, grouped the way the Net Worth view groups accounts. ' +
        'Omits closed accounts and accounts excluded from reports, matching that view. ' +
        'Use this for questions about what net worth is right now and which accounts or groups drive it. ' +
        'Do not use generate_report as a substitute for the current total. For how net worth changed over a period, also call generate_report with reportType net-worth. ' +
        'The on-screen snapshot, when present, is only a hint for the period and headline the user is looking at. ' +
        'liabilities are signed (usually negative). shareOfSidePercent is omitted when that side totals zero. ' +
        'Never show account ids. This tool does not return the net worth report chart.',
      parameters: {
        type: 'object' as const,
        properties: {
          limit: {
            type: 'integer' as const,
            description: 'How many of the largest accounts to return. Defaults to 8. Maximum 20.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const state = store?.getState() as RootState | undefined;

      if (!state) {
        log.error('llmTools/get_net_worth: Redux store is not initialized.');
        return returnToolResponse(msg, { message: 'Net worth is unavailable right now.' });
      }

      const accountsById = accountsSelectors.getReportableAccountsById(state);
      if (!accountsById || accountsById.size === 0) {
        return returnToolResponse(msg, {
          ...buildNetWorthSummary([], accountLimit(args.limit)),
          message: 'No accounts found.',
        });
      }

      const balances = getBalancesByAccountIds(state);
      const accounts: NetWorthSummaryAccount[] = [];
      accountsById.forEach((account) => {
        if (!account.id || account.isClosed || account.isExcludedFromReports) return;
        const balance = balances.get(account.id)?.balance ?? 0;
        accounts.push({
          name: account.name,
          balance,
          group: getGroupForAccount(account),
        });
      });

      const summary = buildNetWorthSummary(accounts, accountLimit(args.limit));
      log.debug('llmTools/get_net_worth', { accountCount: summary.accountCount, netWorth: summary.netWorth });
      return returnToolResponse(msg, summary);
    },
  },
};
